import React, { useMemo, useState } from 'react';
import styled from "styled-components";
import { signVisitorIn, signVisitorOut, VisitorBooking, VisitorSignInState } from "../data/visitor-booking-service";
import Icon, { IconTypes } from '@shared/ui/atoms/icon/Icon';
import { BodyRegular, BodySmall, BodyVerySmall } from '@shared/ui/atoms/fonts/Body';
import Colours from '@shared/ui/atoms/Colours';
import { PrimaryButton } from '@shared/ui/atoms/buttons/PrimaryButton';
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { bookVisitorDesk, cancelVisitorDesk, updateVisitorBookingItem } from "../data/visitorBooking.duck";
import { DialogIdentifiers, openDialogWithPayload } from "@shared/store/dialog.duck";
import { OfficeAndDeskVisitorBookingLabel } from "./OfficeAndDeskVisitorBookingLabel";
import { Period, SideOfDay } from '@features/whereabouts/models/movements.models';
import { tablet } from '@shared/ui/atoms/MediaQueries';
import IconButton from '@shared/ui/molecules/icon-button/IconButton';
import { selectAllOfficeEntities } from "@features/desks/store/advanceHotDeskingSetup.duck";
import { findOfficeInOffices } from "@shared/utils/OfficeHelper";
import { VisitorParkingSpaceLabel } from "./VisitorParkingSpaceLabel";
import { MeetingRoomVisitorBookingLabel } from "./MeetingRoomVisitorBookingLabel";
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';

function getNameColour(state: VisitorSignInState) {
  switch (state) {
    case VisitorSignInState.CHECKED_IN: return Colours.blue;
    case VisitorSignInState.CHECKED_OUT: return Colours.darkGrey;
    default: return Colours.black;
  }
}

function getVisitingUserColour(state: VisitorSignInState) {
  switch (state) {
    case VisitorSignInState.CHECKED_OUT: return Colours.darkGrey;
    default: return Colours.black;
  }
}

export function VisitorBookingLine(props: Props) {
  const {booking} = props;
  const dispatch = useDispatch();
  const allOffices = useSelector(selectAllOfficeEntities);
  const [signInProcessing, setSignInProcessing] = useState(false);

  const isSignedIn = booking.signInState === VisitorSignInState.CHECKED_IN;
  const isSignedOut = booking.signInState === VisitorSignInState.CHECKED_OUT;

  const timeString = useMemo(() => {
    const dateTime = moment();
    dateTime.hours(props.booking.timeDueHours);
    dateTime.minutes(props.booking.timeDueMinutes);
    return dateTime.format('HH:mm');
  }, [props.booking.timeDueMinutes, props.booking.timeDueHours]);

  const isBookingAfterToday = useMemo(() => {
    return moment(booking.date).add(1, 'day').isBefore(moment());
  }, [booking.date]);

  const hasDeskBooking = !!booking.amDeskId || !!booking.pmDeskId;

  const signIn = async () => {
    setSignInProcessing(true);
    try {
      const updatedBooking = await signVisitorIn(booking.id);
      dispatch(updateVisitorBookingItem({visitorBooking: updatedBooking}))
      setSignInProcessing(false);
    } finally {
      setSignInProcessing(false);
    }
  }

  const onLocationSelected = async (selectedOffice?: number, selectedDesk?: number, selectedCarParkSpace?: number, sideOfDay?: SideOfDay) => {
    dispatch(bookVisitorDesk({
      visitorBooking: booking,
      selectedOfficeId: selectedOffice,
      selectedDeskId: selectedDesk,
      selectedCarParkSpace: selectedCarParkSpace,
      sideOfDay: sideOfDay ?? SideOfDay.AllDay,
    }))
  }

  const onSideOfDaySelected = (sideOfDay: Period) => {
    dispatch(openDialogWithPayload({ payload: {
      onLocationSelected: onLocationSelected,
        hideFavouriteControls: true,
        sideOfDay: sideOfDay,
        showCarParkingOption: true,
      } , activeDialog: DialogIdentifiers.ChooseLocation}));
  }

  const bookDesk = () => {
    dispatch(openDialogWithPayload({ payload: { onSideOfDaySelected: onSideOfDaySelected } , activeDialog: DialogIdentifiers.SideOfDaySelectorDialog}));
  }

  const signOut = async () => {
    setSignInProcessing(true);
    try {
      const updatedBooking = await signVisitorOut(booking.id);
      dispatch(updateVisitorBookingItem({visitorBooking: updatedBooking}))
    } finally {
      setSignInProcessing(false);
    }
  }

  const cancelDesk = async () => {
    setSignInProcessing(true);
    try {
      dispatch(cancelVisitorDesk({vistorBooking: booking}));
    } finally {
      setSignInProcessing(false);
    }
  }

  const onEditClicked = async () => {
    const office = findOfficeInOffices(allOffices, booking.officeId);
    if (office) {
      dispatch(openDialogWithPayload({
        payload: { date: moment(booking.date), office: booking.officeId, visitorBooking: booking },
        activeDialog: DialogIdentifiers.AddVisitorDialog
      }));
    }
  }

  return (
    <Container>
      <TimeContainer>
        <Icon icon={IconTypes.Time} size={'medium'} color={'darkGrey'} />
        <BodySmall weight={600} colour={Colours.darkGrey}>{`${timeString}`}</BodySmall>
      </TimeContainer>

      <Border className={"visitorBookingLine__divider"} />

      <NameContainer>
        <BodyRegular weight={600} colour={getNameColour(booking.signInState)}>{booking.visitorName}</BodyRegular>
        {booking.email && <BodyVerySmall weight={400} colour={Colours.darkGrey}>{booking.email}</BodyVerySmall>}
        <BodyVerySmall style={{marginTop: 4}} weight={400} colour={Colours.darkGrey}>{booking.companyName}</BodyVerySmall>
        <OfficeAndDeskVisitorBookingLabel booking={booking} />
        <VisitorParkingSpaceLabel booking={booking} />
        <MeetingRoomVisitorBookingLabel booking={booking} />
        {props.canUserEdit && <>
          <IconButton icon={IconTypes.Edit} text={'button.edit'} onClick={onEditClicked} color={'darkGrey'} style={{width: 100}} />
        </>}
      </NameContainer>

      <Border className={"visitorBookingLine__contentDivider"} />

      <NameContainer>
        <BodyRegular weight={600} style={{marginBottom: 4}} colour={getVisitingUserColour(booking.signInState)}>{booking.visitingUserName}</BodyRegular>
        <BodyVerySmall weight={400} colour={Colours.darkGrey}>{booking.visitingUserTeamName}</BodyVerySmall>
      </NameContainer>

      <ButtonContainer>
        {!isBookingAfterToday && !isSignedIn && !isSignedOut && <PrimaryButton disabled={signInProcessing} text={"visitor-booking.sign-in"} click={signIn} size={ButtonSize.Small} style={{marginBottom: 8}} />}
        {isSignedIn && <PrimaryButton text={"visitor-booking.sign-out"} click={signOut} style={{marginBottom: 8}} size={ButtonSize.Small} />}
        {!isSignedOut && <>
          {props.officeHasAnyChildOffice && <>
            {hasDeskBooking ? <OutlineButton disabled={signInProcessing} borderColour={'grey'} text={"visitor-booking.cancel-desk"} click={cancelDesk} size={ButtonSize.Small} /> : <OutlineButton size={ButtonSize.Small} text={"visitor-booking.book-desk"} click={bookDesk} />}
          </>}
        </>}
      </ButtonContainer>

    </Container>
  )
}

interface Props {
  booking: VisitorBooking;
  officeHasAnyChildOffice: boolean;
  canUserEdit?: boolean;
}

const Container = styled.div`
  background-color: ${Colours.white};
  padding: 24px 32px;
  box-shadow: 0 9px 22px 0 rgba(46, 74, 148, 0.08);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  border-radius: 16px;
  margin-bottom: 16px;
  .visitorBookingLine__divider {
    display: none;
  }
  .visitorBookingLine__contentDivider {
    width: 100%;
    height: 1px;
    margin: 16px 0;
  }
  @media (${tablet}) {
    flex-direction: row;
    .visitorBookingLine__divider {
      display: block;
    }
    .visitorBookingLine__contentDivider {
      width: 1px;
      height: 64px;
      margin: 0 2%;
    }
  }
`

const TimeContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  i {
    width: unset;
    margin-right: 8px;
  }
  @media (${tablet}) {
    flex-direction: column;
    width: 10%;
  }
`

const NameContainer = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;
  @media (${tablet}) {
    width: 35%;
    text-align: left;
  }
`

const Border = styled.div`
  background-color: ${Colours.iconGrey};
  width: 1px;
  height: 64px;
  margin: 0 2%;
`

const ButtonContainer = styled.div`
  margin-top: 20px;
  width: 60%;
  button {
    max-width: 100%;
  }
  @media (${tablet}) {
    margin-top: 0;
    width: 20%;
  }
`
